/**
 * Focus and Verified — `docs/bugs.md` session 4, decisions 14/15.
 *
 * Two flags on an opportunity. Everyone sees and filters on them; only a
 * Manager or an Admin changes them (`canSetOpportunityFlags` in `authz.ts`).
 * Every user today is Menlo staff, so "Menlo only" is enforced by role. When
 * outside users arrive this gets revisited; nothing is built for them now.
 *
 * **One filter vocabulary.** `focus` and `verified` take `yes` or `no`, with
 * the same names and values in the opportunity list
 * (`opportunityFilterSchema`), every analytics endpoint and the analytics CSV
 * export (`analyticsFilterSchema`). A later report filters on them by merging
 * `opportunityFlagFilterSchema` — never a second spelling.
 */

import { z } from 'zod';

/** The wording, exactly, everywhere Focus is explained (decision 14). */
export const FOCUS_HELP_TEXT =
  'Menlo is actively tracking this — someone at Menlo is assigned to it.';

/** What Verified records (decision 15). */
export const VERIFIED_HELP_TEXT = 'Records who verified this opportunity and when.';

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * The request fields that change a flag. `is_verified` is not a column: ticking
 * it stamps `verified_by` (the caller) and `verified_at` (now), and unticking
 * clears both. Neither of those two may be sent directly.
 */
export const OPPORTUNITY_FLAG_FIELDS = ['is_focus', 'is_verified'] as const;
export type OpportunityFlagField = (typeof OPPORTUNITY_FLAG_FIELDS)[number];

export const opportunityFlagWriteSchema = z.object({
  is_focus: z.boolean().optional(),
  is_verified: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

export const FLAG_FILTER_VALUES = ['yes', 'no'] as const;
export type FlagFilterValue = (typeof FLAG_FILTER_VALUES)[number];

export const flagFilterValueSchema = z.enum(FLAG_FILTER_VALUES);

/** The filter keys, in the order the filter bars show them. */
export const OPPORTUNITY_FLAG_FILTER_KEYS = ['focus', 'verified'] as const;
export type OpportunityFlagFilterKey = (typeof OPPORTUNITY_FLAG_FILTER_KEYS)[number];

/** Merged into every filter schema that selects deals. */
export const opportunityFlagFilterSchema = z.object({
  focus: flagFilterValueSchema.optional(),
  verified: flagFilterValueSchema.optional(),
});

export type OpportunityFlagFilter = z.infer<typeof opportunityFlagFilterSchema>;

/** The labels a filter control shows, shared so the list and analytics agree. */
export const FLAG_FILTER_LABELS: Record<
  OpportunityFlagFilterKey,
  { label: string; any: string; yes: string; no: string }
> = {
  focus: { label: 'Focus', any: 'Focus: any', yes: 'Focus', no: 'Not focus' },
  verified: { label: 'Verified', any: 'Verified: any', yes: 'Verified', no: 'Not verified' },
};
